import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AdminDashboard from "./admindashboard";
import EmployeeDetailsModal from "./employeeDetailsModal";
import "./hm-theme.css";

function EmployeeAccountApproval() {
  const [employeesDetails, setEmployeeDetails] = useState([]);
  const [loading, setLoading] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserid, setSelectedUserid] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");

  const token = JSON.parse(localStorage.getItem("data")).token;
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm || selectedCompany !== "all") {
      searchData(0);
    } else {
      getData(currentPage);
    }
  }, [searchTerm, selectedCompany, currentPage]);

  const searchData = async (pageNumber = 0) => {
    setLoading(true);
    try {
      const searchUrl = `/api/employeedetails/searchByCompanyOrNameOrEmail`;
      const requestData = {
        searchTerm,
        companyId: selectedCompany === "all" ? null : selectedCompany,
      };

      const response = await axios.post(searchUrl, requestData, {
        params: { page: pageNumber, disabled: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployeeDetails(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoading(true);
    try {
      const compRes = await axios.get("/api/companydetails/getCompanies", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(compRes.data || []);
      const trans = await axios.get(`/api/employeedetails/getAllEmployees/disabled=true`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setEmployeeDetails(trans.data.content || []);
      setCurrentPage(trans.data.number || 0);
      setTotalPages(trans.data.totalPages || 0);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const openProfileModal = (userid) => {
    setSelectedUserid(userid);
    setIsModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setSelectedUserid(null);
    setIsModalOpen(false);
    getData(currentPage);
  };

  const handleApprovalChange = async (e, userid) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const requestData = {
        disabled: false,
        userid: userid,
      };
      await axios.post("/api/employeedetails/approve-account", requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Account Approved Successfully");
      getData(currentPage);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  return (
    <div>
      <AdminDashboard />
      {loading && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">how_to_reg</i>
                Employee Account Approvals
              </h2>
              <p className="hm-page-subtitle">Review pending employee registrations and approve accounts</p>
            </div>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_alt</i>
                Search & Filter
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">Search Employee</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control ps-4"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                      search
                    </i>
                  </div>
                </div>

                <div className="col-md-5">
                  <label className="form-label small fw-semibold">Filter by Company</label>
                  <select
                    className="form-select"
                    value={selectedCompany || "all"}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="all">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyname} ({company.companycode})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Grid Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">people_outline</i>
                Pending Approvals
              </h3>
              <span className="hm-badge hm-badge-warning">{employeesDetails.length} pending</span>
            </div>
            <div className="hm-card-body">
              {employeesDetails.length === 0 ? (
                <div className="hm-empty-state">
                  <i className="material-icons">check_circle_outline</i>
                  <p>No pending account approvals</p>
                </div>
              ) : (
                <div className="row g-4">
                  {employeesDetails.map((accd) => (
                    <div className="col-md-6 col-lg-4" key={accd.id}>
                      <div
                        className="hm-card h-100 p-3 d-flex flex-column justify-content-between"
                        style={{ cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s" }}
                        onClick={() => openProfileModal(accd.userid)}
                      >
                        <div className="text-center mb-3">
                          {accd.employeeProfileImage?.image ? (
                            <img
                              src={`${accd.employeeProfileImage?.imageMeta},${accd.employeeProfileImage?.image}`}
                              alt="Profile"
                              className="rounded-circle mb-2"
                              style={{ width: "80px", height: "80px", objectFit: "cover" }}
                            />
                          ) : (
                            <div
                              className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                              style={{ width: "80px", height: "80px", background: "rgba(47, 58, 136, 0.1)", color: "#2f3a88", fontSize: "2.2rem" }}
                            >
                              <i className="material-icons">person</i>
                            </div>
                          )}
                          <h5 className="fw-bold mb-1">{accd.name} {accd.lastname}</h5>
                          <span className="hm-badge hm-badge-primary">{accd.companycode}</span>
                        </div>

                        <div className="small text-muted mb-3 border-top pt-2">
                          <div className="mb-1"><i className="material-icons align-middle me-1" style={{ fontSize: "1rem" }}>email</i>{accd.email}</div>
                          <div><i className="material-icons align-middle me-1" style={{ fontSize: "1rem" }}>phone</i>{accd.phoneno || "N/A"}</div>
                        </div>

                        <div className="d-flex justify-content-end pt-2 border-top">
                          <button
                            className="btn btn-success btn-sm rounded-pill px-3 d-flex align-items-center gap-1"
                            onClick={(e) => handleApprovalChange(e, accd.userid)}
                          >
                            <i className="material-icons" style={{ fontSize: "1rem" }}>check</i>
                            Approve Account
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination className="hm-pagination">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                    {renderPageNumbers()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {isModalOpen && (
        <EmployeeDetailsModal
          isOpen={isModalOpen}
          onRequestClose={handleCloseProfileModal}
          userid={selectedUserid}
        />
      )}
    </div>
  );
}

export default EmployeeAccountApproval;